import React, { CSSProperties, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

import {
  ArrowRightOutlined,
  BarChartOutlined,
  FormOutlined,
  RocketOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Button, Card, Result, Skeleton, Space, Typography } from 'antd';

import { ClientSetupGuideContext } from '../components/ClientSetupGuideModal';
import { BuildingOutlined, ClinicOutlined } from '../components/MabelIcons';
import { UserContext } from '../contexts/UserContext';
import { useGetClientOrganizationDetailsQuery } from '../graphql/generated';

const pageStyle: CSSProperties = {
  margin: '0 auto',
  maxWidth: 1200,
};

const welcomeCardStyle: CSSProperties = {
  background: 'linear-gradient(135deg, #f0f5ff 0%, #f9f0ff 100%)',
  border: '1px solid #d6e4ff',
  borderRadius: 12,
  marginBottom: 32,
  overflow: 'hidden',
};

const navigationCardStyle: CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
};

const navigationLinkStyle: CSSProperties = {
  color: 'inherit',
  display: 'block',
};

const iconStyle: CSSProperties = {
  flex: '0 0 auto',
  alignItems: 'center',
  background: '#f0f5ff',
  borderRadius: 8,
  color: '#2f54eb',
  display: 'flex',
  fontSize: 20,
  height: 40,
  justifyContent: 'center',
  width: 40,
};

const navigationContentStyle: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 16,
  minWidth: 0,
};

const navigationTextStyle: CSSProperties = {
  flex: '1 1 360px',
  minWidth: 180,
};

const navigationItems = [
  {
    description: 'Upload the CMS files needed for BNL and SVA processing.',
    icon: <UploadOutlined />,
    path: '/file-uploads',
    title: 'File Uploads',
  },
  {
    description: 'Review and maintain the practices in your organization.',
    icon: <ClinicOutlined />,
    path: '/client-practices',
    title: 'Practices',
  },
  {
    description: 'Manage your ACO information, branding, and contact details.',
    icon: <BuildingOutlined />,
    path: '/aco-entities',
    title: 'ACO Entities',
  },
  {
    description: 'Preview the forms and outreach materials available to you.',
    icon: <FormOutlined />,
    path: '/template-previews',
    title: 'Forms',
  },
  {
    description:
      'Explore reporting and performance data for your organization.',
    icon: <BarChartOutlined />,
    path: '/dashboards',
    title: 'Dashboards',
  },
];

const ClientHomePage: React.FC = () => {
  const { user } = useContext(UserContext);
  const { openSetupGuide } = useContext(ClientSetupGuideContext);
  const { data, loading, error } = useGetClientOrganizationDetailsQuery({
    variables: { user_id: user?.user_id || '' },
    skip: !user?.user_id,
  });
  const organization = data?.users_by_pk?.client_organization;
  const firstName = user?.claims?.name?.trim().split(/\s+/)[0];

  if (loading) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving your organization"
        subTitle={error.message}
      />
    );
  if (!organization)
    return (
      <Result
        status="warning"
        title="No organization profile found"
        subTitle="Your account is not attached to a client organization."
      />
    );

  return (
    <>
      <Helmet>
        <title>Home | Mabel</title>
      </Helmet>
      <main style={pageStyle}>
        <Card
          bordered={false}
          bodyStyle={{ padding: '40px 44px' }}
          style={welcomeCardStyle}
        >
          <Typography.Text
            style={{
              color: '#2f54eb',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: 0.6,
            }}
          >
            {organization.organization_name.toUpperCase()}
          </Typography.Text>
          <Typography.Title level={1} style={{ marginBottom: 8, marginTop: 8 }}>
            Welcome{firstName ? `, ${firstName}` : ''}
          </Typography.Title>
          <Typography.Paragraph
            style={{ fontSize: 17, marginBottom: 24, maxWidth: 720 }}
            type="secondary"
          >
            {organization.organization_description ||
              'Manage your organization, review outreach materials, and explore your data from one place.'}
          </Typography.Paragraph>
          <div style={{ fontSize: 16 }}>
            <Typography.Text strong style={{ fontSize: 16 }}>
              Just Getting Started?{' '}
            </Typography.Text>
            <Button
              onClick={openSetupGuide}
              style={{ fontSize: 16, height: 'auto', padding: 0 }}
              type="link"
            >
              <RocketOutlined style={{ transform: 'rotate(45deg)' }} /> See our
              setup guide <ArrowRightOutlined />
            </Button>
          </div>
        </Card>

        <Typography.Title level={2} style={{ marginBottom: 24 }}>
          What would you like to do?
        </Typography.Title>

        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {navigationItems.map((item) => (
            <Link
              aria-label={item.title}
              key={item.path}
              style={navigationLinkStyle}
              to={item.path}
            >
              <Card
                bodyStyle={{ padding: '16px 20px' }}
                hoverable
                style={navigationCardStyle}
              >
                <div style={navigationContentStyle}>
                  <div style={iconStyle}>{item.icon}</div>
                  <div style={navigationTextStyle}>
                    <Typography.Title level={4} style={{ marginBottom: 2 }}>
                      {item.title}
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      {item.description}
                    </Typography.Text>
                  </div>
                  <Typography.Text
                    style={{
                      color: '#2f54eb',
                      flex: '0 0 auto',
                      fontWeight: 500,
                    }}
                  >
                    Open <ArrowRightOutlined />
                  </Typography.Text>
                </div>
              </Card>
            </Link>
          ))}
        </Space>
      </main>
    </>
  );
};

export default ClientHomePage;
